// Server-side phase-checklist derivation.
//
// components/HomeV2/lib.js (PHASE_CHECKLIST / getRuntimePhases / isActionChecked)
// is a CLIENT module ('use client' + React/DOM imports), so it cannot be
// imported on the server. This is a minimal duplicate of exactly that
// derivation, used to surface a placement's open checklist tasks in a day-list's
// "Interviews vandaag" section. KEEP IN SYNC if the checklist shape changes.
//
// openTaskEntries staat hier NIET meer dubbel: dit bestand is puur (geen React,
// geen DB), dus components/HomeV2/ProcesCard.js importeert deze functie en geeft
// er getRuntimePhases() aan mee.

import { effectivePhase, ensurePhaseFloors } from '../deal-phase.mjs';

// Static per-track phase -> actions map (mirror of components/HomeV2/lib.js
// PHASE_CHECKLIST). Director runtime customizations are merged OVER this via
// mergePhases (same as the client's getRuntimePhases).
export const PHASE_CHECKLIST = {
  bd: [
    { phase: 'Voorbereiding',  actions: ['Lead gevonden en opgeslagen', 'Bedrijf en DMU geresearcht', 'Direct nummer P&L verantwoordelijke gevonden', 'LinkedIn profiel bekeken'] },
    { phase: 'Eerste Contact', actions: ['Intro en probe doorlopen via IPMERC', 'FABs gebruikt en match gepresenteerd', 'Rules of engagement bevestigd'] },
    { phase: 'Propositie',     actions: ['Raamovereenkomst pdf email verstuurd naar prospect', 'Voorstel Retained verstuurd naar prospect', 'Voorstel telefonisch opgevolgd', 'Bezwaren behandeld via WRIOC'] },
    { phase: 'Deal',           actions: ['Mondeling akkoord ontvangen', 'Contract verstuurd', 'Getekend contract ontvangen'] },
  ],
  // fromStatus: de statusondergrens van de fase, zie lib/deal-phase.mjs.
  placement: [
    { phase: 'Intake',          fromStatus: '', actions: ['Hot job criteria vastgesteld', 'Job kwalificatie ingevuld via IPMERC', 'Zoekopdracht aangemaakt in CRM'] },
    { phase: 'Search',          fromStatus: '', actions: ['Longlist aangemaakt', 'Kandidaten benaderd via IPMERC', 'Kandidaat kwalificatie ingevuld', 'CV ontvangen en geaccordeerd'] },
    { phase: 'Interviewronden', fromStatus: 'IV1', actions: ['CV naar klant verstuurd en telefonisch opgevolgd', 'Interview gepland en bevestigd', 'Pre- en post-interview calls gedaan en feedback opgehaald'] },
    { phase: 'Closing',         fromStatus: 'Aanbieding arbeidscontract', actions: ['Pre-close gedaan: theoretisch aanbod gecheckt', 'Bezwaren behandeld', 'Aanbieding ontvangen van klant', 'Aanbieding uitgebracht en startdatum bevestigd'] },
  ],
  sourcing: [
    { phase: 'Contact',        actions: ['Kandidaat benaderd via IPMERC', 'Gesproken en interesse gepeild', 'Samenwerking afgesproken'] },
    { phase: 'Kwalificatie',   actions: ['Kandidaat kwalificatie ingevuld', 'CV ontvangen en geaccordeerd', 'Contactgegevens compleet (tel + e-mail)'] },
    { phase: 'Beschikbaarheid', actions: ['Beschikbaarheid / interviewslots vastgelegd', 'CV verstuurd naar klant'] },
  ],
};

// Merge director runtime customizations UNDER the static defaults - the exact
// shape components/HomeV2/lib.js getRuntimePhases() produces. `runtime` is the
// /api/phases blob (phases.data, ideally already through ensureRequiredActions),
// or null when no customizations are stored. De fase-ondergrenzen worden hier
// aangevuld, zodat elke lezer ze draagt ook als de opgeslagen blob ze mist.
export function mergePhases(runtime) {
  return runtime && typeof runtime === 'object'
    ? ensurePhaseFloors({ ...PHASE_CHECKLIST, ...runtime })
    : PHASE_CHECKLIST;
}

// Mirror of components/HomeV2/lib.js isActionChecked: the phase-scoped key
// ("phaseIdx:action") wins; a bare-action key is the legacy fallback. Renamed
// checklist actions also fall back to their pre-rename wording (see
// LEGACY_ACTION_ALIASES in the client mirror) so an old tick keeps counting.
const LEGACY_ACTION_ALIASES = {
  'Aanbieding uitgebracht en startdatum bevestigd': ['Offer uitgebracht en startdatum bevestigd'],
  'Lead gevonden en opgeslagen': ['Lead geregistreerd'],
  // 'gebeld' staat nergens meer in de app (eigenaarsregel 18 okt 2026).
  'Voorstel telefonisch opgevolgd': ['Voorstel nagebeld'],
  'Kandidaten benaderd via IPMERC': ['Kandidaten gebeld via IPMERC'],
  'CV naar klant verstuurd en telefonisch opgevolgd': ['CV naar klant verstuurd en nagebeld'],
  'Kandidaat benaderd via IPMERC': ['Kandidaat gebeld via IPMERC'],
};
export function isActionChecked(actions, phaseIdx, action) {
  const pk = `${phaseIdx}:${action}`;
  if (pk in actions) return !!actions[pk];
  if (action in actions) return !!actions[action];
  for (const legacy of (LEGACY_ACTION_ALIASES[action] || [])) {
    const lpk = `${phaseIdx}:${legacy}`;
    if (lpk in actions) return !!actions[lpk];
    if (legacy in actions) return !!actions[legacy];
  }
  return false;
}

// Open (unchecked) checklist tasks for a deal's EFFECTIVE current phase and
// every phase it really passed through. DE ene afleiding: de client-kant
// (components/HomeV2/ProcesCard.js openTaskEntries) roept deze functie aan met
// getRuntimePhases(), dus er is geen tweede telling meer.
//
// Fasen die het proces OVERSLOEG doordat de status er overheen liep tellen niet
// mee (eigenaarsbesluit): niemand heeft die stappen vergeten, ze waren er nooit
// aan de beurt. Dat zijn precies de indexen in `skipped`.
//
// Retourneert [{ phaseIdx, action, phase, missed }]; `missed` is waar voor een
// fase die het proces wel doorliep maar met open stappen achterliet.
// `phasesByTrack` is de samengevoegde fasenkaart uit mergePhases (standaard
// PHASE_CHECKLIST).
export function openTaskEntries(deal, phasesByTrack = PHASE_CHECKLIST) {
  const phases = (phasesByTrack || PHASE_CHECKLIST)[deal?.track];
  if (!phases) return [];
  const pc = deal.phaseChecklist || { phaseIndex: 0, actions: {} };
  const acts = pc.actions || {};
  const { stored, index, skipped } = effectivePhase(deal, phases);
  const overgeslagen = new Set(skipped);
  const out = [];
  for (let i = 0; i <= index; i++) {
    if (overgeslagen.has(i)) continue;
    for (const a of (phases[i].actions || [])) {
      if (!isActionChecked(acts, i, a)) out.push({ phaseIdx: i, action: a, phase: phases[i].phase, missed: i < stored });
    }
  }
  return out;
}
